import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import {
  MapPin,
  House,
  BedDouble,
  Bath,
  Users,
  ArrowLeft,
  Check,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  Smile,
} from "lucide-react";
import NavBar from "../components/Navigationbar";
import Footer from "../components/Footer";

const API_URL = import.meta.env.VITE_API_URL;

const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeProperty = (a) => {
  const imagesArray = Array.isArray(a?.images) ? a.images : [];

  const images = imagesArray
    .map((img) =>
      typeof img === "string" ? img : img?.url || img?.path || null
    )
    .filter(Boolean)
    .map((url) =>
      typeof url === "string" && url.startsWith("http://")
        ? url.replace("http://", "https://")
        : url
    );

  const status = (a?.status || "").toLowerCase();
  const available =
    a?.available !== undefined
      ? a.available
      : status === "available" || status === "vacant";

  return {
    ...a,
    images,
    available,
    propertyType:
      a?.propertyType || a?.accommodationType || a?.accommodation_type || "",
    ownerPhone: a?.ownerPhone || a?.owner_phone || "",
    ownerEmail: a?.ownerEmail || a?.owner_email || "",
  };
};

function PropertyDetails() {
  const [currentImage, setCurrentImage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const deltaXRef = useRef(0);

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // If we came from the Properties page, we already have the property in state.
  const [property, setProperty] = useState(
    location.state?.property ? normalizeProperty(location.state.property) : null
  );
  const [loading, setLoading] = useState(!location.state?.property);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // If we already have the property or no id in URL, do nothing.
    if (property || !id) return;

    const fetchProperty = async () => {
      try {
        // Use the public accommodations list and find the one with this id
        const res = await fetch(`${API_URL}/api/public/accommodations`);

        if (!res.ok) {
          setNotFound(true);
          return;
        }

        const data = await res.json();
        const list = Array.isArray(data.accommodations)
          ? data.accommodations
          : [];

        const found = list.find((item) => {
          if (String(item.id) !== String(id)) return false;

          const ls = (
            item.listingStatus ||
            item.listing_status ||
            "listed"
          ).toLowerCase();
          return ls === "listed";
        });

        if (!found) {
          setNotFound(true);
          return;
        }

        setProperty(normalizeProperty(found));
      } catch (err) {
        console.error("Error loading property", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id, property]);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <p>Loading property…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !property) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <p className="mb-4">Property not found.</p>
          <Button onClick={() => navigate("/properties")}>
            Back to listings
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Owner contact coming from backend (support multiple naming styles)
  const ownerPhone =
    property?.ownerPhone ||
    property?.owner_phone ||
    property?.owner?.phone ||
    "";

  const ownerEmail =
    property?.ownerEmail ||
    property?.owner_email ||
    property?.owner?.email ||
    "";

  const slug = slugify(property.title || "property");

  const images = Array.isArray(property.images) ? property.images : [];

  const hasImages = images.length > 0;

  // Carousel image
  const nextImage = () => {
    if (!hasImages || images.length < 2) return;
    setCurrentImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    if (!hasImages || images.length < 2) return;
    setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const onPointerDown = (e) => {
    if (!hasImages || images.length < 2) return;
    setIsDragging(true);
    startXRef.current = e.clientX;
    deltaXRef.current = 0;
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    deltaXRef.current = e.clientX - startXRef.current;
  };

  const onPointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const dx = deltaXRef.current;
    const threshold = 80;

    if (dx > threshold) {
      prevImage();
    } else if (dx < -threshold) {
      nextImage();
    }

    deltaXRef.current = 0;
  };

  // Status styles for accommodation
  const getStatusStyle = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "vacant":
        return "bg-[#D2138C] hover:bg-[#D2138C] text-white";
      case "occupied":
        return "bg-gray-200 hover:bg-gray-200 text-gray-700";
      case "coming soon":
        return "bg-amber-100 hover:bg-amber-100 text-amber-800 border border-amber-200";
      case "pending":
        return "bg-blue-100 hover:bg-blue-100 text-blue-800 border border-blue-200";
      default:
        return "bg-gray-100 hover:bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <NavBar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate("/properties")}
          className="flex items-center gap-2 text-gray-600 hover:text-[#D2138C] transition-colors mb-4 cursor-pointer">
          <ArrowLeft className="w-5 h-5" />
          Back to listings
        </button>

        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/properties">Properties</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{property.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="overflow-hidden rounded-2xl w-full">
              <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] lg:h-96">
                {hasImages ? (
                  <div
                    className="absolute inset-0 overflow-hidden rounded-2xl bg-transparent touch-pan-y select-none"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onPointerLeave={onPointerUp}
                    style={{ cursor: images.length > 1 ? "grab" : "default" }}>
                    <div
                      className={[
                        "h-full w-full flex",
                        isDragging
                          ? ""
                          : "transition-transform duration-300 ease-out",
                      ].join(" ")}
                      style={{
                        transform: `translateX(-${currentImage * 100}%)`,
                      }}>
                      {images.map((src, idx) => (
                        <div key={idx} className="h-full w-full flex-shrink-0">
                          <img
                            src={src}
                            alt={`${property.title} - Image ${idx + 1}`}
                            className="w-full h-full object-cover select-none"
                            draggable={false}
                            loading="eager"
                            onError={(e) => {
                              e.currentTarget.src =
                                "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%236b7280' font-size='20'>No Image</text></svg>";
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                    No image available
                  </div>
                )}

                {/* Navigation Buttons */}
                {hasImages && images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                      aria-label="Previous image">
                      <ChevronLeft className="w-6 h-6 text-gray-800" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                      aria-label="Next image">
                      <ChevronRight className="w-6 h-6 text-gray-800" />
                    </button>
                  </>
                )}

                {/* Dots indicator */}
                {hasImages && images.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                    {images.map((_, index) => (
                      <div
                        key={index}
                        className={`h-1.5 w-1.5 rounded-full ${
                          index === currentImage
                            ? "bg-[#D2138C]"
                            : "bg-white/70"
                        }`}
                      />
                    ))}
                  </div>
                )}

                {/* Status badge */}
                <div
                  className={`absolute top-3 right-3 font-medium text-white px-4 py-2 rounded-full ${getStatusStyle(
                    property.status ||
                      (property.available ? "vacant" : "occupied")
                  )}`}>
                  {property.status
                    ? property.status.charAt(0).toUpperCase() +
                      property.status.slice(1)
                    : property.available
                    ? "Vacant"
                    : "Occupied"}
                </div>
              </div>

              {/* Thumbnail Strip */}
              {hasImages && images.length > 1 && (
                <div className="p-3 sm:p-4 flex gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch]">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        currentImage === index
                          ? "border-[#D4879C]"
                          : "border-white/0 hover:border-white/30"
                      }`}>
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title and Location */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold mb-3 sm:mb-4">
                  {property.title}
                </h1>
                <div className="flex items-center gap-1 text-gray-600">
                  <MapPin className="w-5 h-5" />
                  <span>{property.location}</span>
                </div>
              </div>
            </div>

            {/* Property Stats */}
            <div className="rounded-xl p-0 space-y-3">
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-700">
                {/* Bedrooms */}
                <div className="flex items-center">
                  <div className="w-9 h-9 flex items-center justify-center">
                    <BedDouble className="w-5 h-5 text-[#D2138C]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-900">
                      {property.bedrooms} Bedrooms
                    </p>
                  </div>
                </div>

                {/* Bathrooms */}
                <div className="flex items-center">
                  <div className="w-9 h-9 flex items-center justify-center">
                    <Bath className="w-5 h-5 text-[#D2138C]" />
                  </div>
                  <p className="text-sm text-gray-900">
                    {property.bathrooms} Bathrooms
                  </p>
                </div>

                {/* Capacity */}
                <div className="flex items-center">
                  <div className="w-9 h-9 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#D2138C]" />
                  </div>
                  <p className="text-sm text-gray-900">
                    {property.capacity}{" "}
                    {property.capacity === 1 ? "Person" : "People"}
                  </p>
                </div>

                {/* Property Type */}
                <div className="flex items-center">
                  <div className="w-9 h-9 flex items-center justify-center">
                    <House className="w-5 h-5 text-[#D2138C]" />
                  </div>
                  <p className="text-sm text-gray-900">
                    {property.propertyType}
                  </p>
                </div>

                {/* Gender */}
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 flex items-center justify-center">
                    <Smile className="w-5 h-5 text-[#D2138C]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-900">{property.gender}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="rounded-2xl p-0">
              <h2 className="text-gray-900 text-xl font-semibold mb-4">
                About This Property
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {property.description}
              </p>
            </div>

            {/* Features */}
            <div className="rounded-2xl p-0">
              <h2 className="text-gray-900 text-xl font-semibold mb-4">
                Features
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(property.features || []).map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-5 h-5 text-[#D2138C]" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div className="rounded-2xl p-0">
              <h2 className="text-gray-900 text-xl font-semibold mb-4">
                Amenities
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(property.amenities || []).map((amenity, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-5 h-5 text-[#D2138C]" />
                    </div>
                    <span className="text-gray-700">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl p-0 lg:sticky lg:top-20 lg:pl-2">
              <h3 className="text-gray-900 text-lg font-medium mb-4">
                Interested in this property?
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                Discover more about our supported living homes designed to
                provide comfort, safety, and independence for every resident.
              </p>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 p-3">
                  <Phone className="w-5 h-5 text-[#d4879c]" />
                  <p className="text-gray-900 text-sm">
                    {ownerPhone || "No Number"}
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3">
                  <Mail className="w-5 h-5 text-[#d4879c]" />
                  <p className="text-gray-900 text-sm">
                    {ownerEmail || "No Email"}
                  </p>
                </div>
              </div>

              <Button
                onClick={() =>
                  navigate(`/properties/${property.id}/${slug}/enquire`, {
                    state: { property },
                  })
                }
                className="w-full bg-[#D2138C] hover:bg-pink-700 text-white rounded-xl h-11 mb-3">
                Enquire Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PropertyDetails;
